import { z } from 'zod';

import { AddressSchema, CommonAddressSchema } from './addressSchema.ts';
import { AuditResponseSchema } from './auditSchema.ts';
import { DayJsSchema } from './commonSchema.ts';
import {
  createPaginatedResponseSchema,
  PagedRequestSchema,
} from './pagingSchema.ts';
import { PriceSchema } from './pricingSchema.ts';

export const PurchaseOrderTypeEnum = z.enum([
  'Undefined',
  'Manual',
  'Automatic',
]);

export const PurchaseOrderNumberFormatEnum = z.enum([
  'Expense',
  'Replenishment',
  'PunchOut',
  'CallOff',
]);

export type PurchaseOrderNumberFormat = z.infer<
  typeof PurchaseOrderNumberFormatEnum
>;

const NonCallOffPurchaseOrderNumberFormatEnum =
  PurchaseOrderNumberFormatEnum.exclude(['CallOff']);

export const PurchaseOrderIdSchema = z.object({
  purchaseOrderId: z.uuid(),
});

const PurchaseOrderServiceLocationSchema = z.object({
  serviceLocationId: z.uuid(),
  serviceLocationName: z.string(),
  serviceLocationCode: z.string().nullish(),
});

const PurchaseOrderDeliverySchema = z
  .object({
    receivedBy: z.string(),
    note: z.string(),
    quantity: z.int(),
    deliveryDate: z.string(),
  })
  .array()
  .nullish();

export const PurchaseOrderCommonItemSchema = z.object({
  orderId: z.uuid().nullish(),
  jobId: z.uuid().nullish(),
  itemId: z.uuid().nullish(),
  partId: z.uuid().nullish(),
  sku: z.string().nullish(),
  partNumber: z.string().nullish(),
  description: z.string().nullish(),
  quantity: z.int(),
  unitPrice: PriceSchema,
  totalPrice: PriceSchema,
  stockTrack: z.boolean(),
  stockUnits: z
    .array(
      z.object({
        id: z.uuid(),
        isDelivered: z.boolean(),
        dateDelivered: z.string(),
      })
    )
    .nullish(),
  latestDateDelivered: z.string().nullish(),
  shortShipped: z.boolean().nullish(),
  delivery: PurchaseOrderDeliverySchema,
  orderReference: z.string(),
  orderStatus: z.string(),
  jobReference: z.string(),
  jobStatus: z.string(),
});

export const PurchaseOrderCommonNoteSchema = z.object({
  note: z.string().nullish(),
  createdDate: z.string(),
  createdBy: z.string().nullish(),
});

export const PurchaseOrderNoteSchema = PurchaseOrderCommonNoteSchema.extend({
  id: z.uuid(),
});

export const PurchaseOrderItemSchema = PurchaseOrderCommonItemSchema.extend({
  id: z.uuid(),
  quantityReceived: z.int(),
}).omit({ itemId: true });

export const PurchaseOrderNoteRequestSchema = z.object({
  purchaseOrderId: z.uuid(),
  note: PurchaseOrderCommonNoteSchema,
});

export const PurchaseOrderDeliveryDetailsUpdateSchema = z.object({
  purchaseOrderId: z.uuid(),
  receivedBy: z.string().optional(),
  deliveryNote: z.string().optional(),
});

export const UpdatePurchaseOrderItemSchema = z.object({
  id: z.uuid(),
  description: z.string().trim().min(1),
});

export const UpdatePurchaseOrderSchema = z.object({
  purchaseOrderId: z.uuid(),
  supplierId: z.uuid().optional(),
  supplierName: z.string().trim().min(1).optional(),
  deliveryDate: z.coerce.date<Date>().or(DayJsSchema).optional(),
  items: UpdatePurchaseOrderItemSchema.array().min(1).optional(),
});

export const PurchaseOrderFormItemSchema = z.object({
  partNumber: z.string().min(1),
  sku: z.string().min(1),
  description: z.string().min(1).nullish(),
  quantity: z.number().min(1),
  unitPrice: z.object({
    exTax: z.number().min(0).multipleOf(0.01),
    incTax: z.number().min(0).multipleOf(0.01),
    tax: z.number().min(0).multipleOf(0.01),
  }),
  stockTrack: z.boolean(),
  invoice: z.string().nullish(),
  supplier: z
    .object({
      supplierId: z.uuid(),
      supplierName: z.string().nullish(),
    })
    .nullish(),
  partId: z.uuid().nullish(),
  jobId: z.uuid().nullish(),
  orderId: z.uuid().nullish(),
  itemId: z.uuid().nullish(),
  delivery: PurchaseOrderDeliverySchema,
  orderReference: z.string().nullish(),
  orderStatus: z.string().nullish(),
  jobReference: z.string().nullish(),
  jobStatus: z.string().nullish(),
});

export const PurchaseOrderStatusEnum = z.enum([
  'Draft',
  'Open',
  'Completed',
  'Cancelled',
  'ShortShipped',
]);

export const PurchaseOrderFormSchema = z.object({
  serviceLocation: PurchaseOrderServiceLocationSchema.omit({
    serviceLocationName: true,
  }).extend({
    serviceLocationName: z.string().nullish(),
  }),
  supplier: z.object({
    id: z.uuid(),
    name: z.string().nullish(),
  }),
  duplicateNumber: z.boolean(),
  currencyCode: z.string().nullish(),
  paymentTerms: z.string().nullish(),
  items: z.array(PurchaseOrderFormItemSchema).nullish(),
  deliveryDate: z.coerce.date<Date>().or(DayJsSchema).optional(),
  startDate: z.coerce.date<Date>().or(DayJsSchema).optional(),
  endDate: z.coerce.date<Date>().or(DayJsSchema).optional(),
  notes: z.array(PurchaseOrderCommonNoteSchema).nullish(),
  deliveryNote: z.string().nullish(),
  numberFormat: PurchaseOrderNumberFormatEnum,
  orderId: z.uuid().nullish(),
  linkToOrderOnCreation: z.boolean().nullish(),
  orderReference: z.string().nullish(),
  orderStatus: z.string().nullish(),
  jobReference: z.string().nullish(),
  jobStatus: z.string().nullish(),
  status: PurchaseOrderStatusEnum.optional(),
});

export const CreatePurchaseOrderItemSchema = PurchaseOrderFormItemSchema.omit({
  unitPrice: true,
}).extend({
  unitPrice: PriceSchema,
  totalPrice: PriceSchema,
});

export const CreatePurchaseOrderFromJobFormSchema =
  PurchaseOrderFormSchema.omit({
    items: true,
    orderId: true,
    supplier: true,
  }).extend({
    orderId: z.uuid(),
    jobId: z.uuid(),
    supplier: z
      .object({
        id: z.uuid(),
        name: z.string().nullish(),
      })
      .nullish(),
    oneTimeSupplier: z
      .object({
        isOneTimeSupplier: z.boolean().optional(),
        businessNumber: z.string().trim().nullish(),
        name: z.string().trim().max(30).nullish(),
      })
      .nullish(),
    items: PurchaseOrderFormItemSchema.extend({
      partId: z.uuid(),
      jobId: z.uuid(),
      orderId: z.uuid(),
      itemId: z.uuid(),
    })
      .array()
      .min(1),
  });

export const CreatePurchaseOrderFromJobRequestSchema =
  CreatePurchaseOrderFromJobFormSchema.omit({
    duplicateNumber: true,
  }).extend({
    items: CreatePurchaseOrderItemSchema.array(),
    deliveryAddress: CommonAddressSchema.omit({ geoLocation: true })
      .extend({
        geoLocation: z
          .object({
            lat: z.number(),
            lng: z.number(),
          })
          .nullish(),
      })
      .optional(),
    totalPrice: PriceSchema,
    type: PurchaseOrderTypeEnum.nullish(),
    status: PurchaseOrderStatusEnum.optional(),
  });

export type CreatePurchaseOrderFromJobFormModel = z.infer<
  typeof CreatePurchaseOrderFromJobFormSchema
>;

export const CreatePurchaseOrderSchema = PurchaseOrderFormSchema.omit({
  items: true,
  duplicateNumber: true,
}).extend({
  items: CreatePurchaseOrderItemSchema.array(),
  deliveryAddress: CommonAddressSchema.omit({ geoLocation: true }).extend({
    geoLocation: z
      .object({
        lat: z.number(),
        lng: z.number(),
      })
      .nullish(),
  }),
  totalPrice: PriceSchema,
  type: PurchaseOrderTypeEnum.nullish(),
  status: PurchaseOrderStatusEnum.optional(),
});

const GetPurchaseOrderBaseSchema = z.object({
  id: z.uuid(),
  number: z.string().nullish(),
  status: z.string().nullish(),
  supplier: z.object({
    id: z.uuid(),
    name: z.string().nullish(),
    address: CommonAddressSchema,
  }),
  currencyCode: z.string().nullish(),
  paymentTerms: z.string().nullish(),
  items: PurchaseOrderItemSchema.array().nullish(),
  totalPrice: PriceSchema,
  deliveryDate: z.string().nullish(),
  startDate: z.coerce.date<Date>().nullish(),
  endDate: z.coerce.date<Date>().nullish(),
  receivedBy: z.string().nullish(),
  deliveryAddress: CommonAddressSchema,
  serviceLocation: z.object({
    serviceLocationId: z.uuid(),
    serviceLocationName: z.string().nullish(),
    serviceLocationCode: z.string().nullish(),
  }),
  notes: z.array(PurchaseOrderCommonNoteSchema).nullish(),
  deliveryNote: z.string().nullish(),
  type: PurchaseOrderTypeEnum,
  numberFormat: PurchaseOrderNumberFormatEnum,
});

const GetCallOffPurchaseOrderSchema = GetPurchaseOrderBaseSchema.extend({
  startDate: z.coerce.date<Date>(),
  endDate: z.coerce.date<Date>(),
  numberFormat: z.literal('CallOff'),
});

const GetNonCallOffPurchaseOrderSchema = GetPurchaseOrderBaseSchema.extend({
  deliveryDate: z.string().trim().min(1),
  numberFormat: NonCallOffPurchaseOrderNumberFormatEnum,
});

export const GetPurchaseOrderSchema = z.discriminatedUnion('numberFormat', [
  GetCallOffPurchaseOrderSchema,
  GetNonCallOffPurchaseOrderSchema,
]);

const PurchaseOrderBaseSchema = z.object({
  id: z.uuid(),
  number: z.string().nullish(),
  orderNumber: z.string().nullish(),
  supplier: z.object({
    id: z.uuid(),
    name: z.string().nullish(),
    address: z.object({
      text: z.string().nullish(),
      addressLine1: z.string().nullish(),
      addressLine2: z.string().nullish(),
      addressLine3: z.string().nullish(),
      addressLine4: z.string().nullish(),
      postalCode: z.string().nullish(),
      country: z.string().nullish(),
      countryCode: z.string().nullish(),
      timeZone: z.string().nullish(),
    }),
  }),
  items: z.array(PurchaseOrderItemSchema).nullish(),
  status: z
    .array(
      z.object({
        status: PurchaseOrderStatusEnum,
        isCurrent: z.boolean(),
      })
    )
    .nullish(),
  totalPrice: PriceSchema,
  currencyCode: z.string().nullish(),
  paymentTerms: z.string().optional(),
  deliveryDate: z.coerce.date<Date>().optional(),
  startDate: z.coerce.date<Date>().optional(),
  endDate: z.coerce.date<Date>().optional(),
  deliveryNote: z.string().optional(),
  receivedBy: z.string().optional(),
  deliveryAddress: AddressSchema.optional(),
  serviceLocation: PurchaseOrderServiceLocationSchema.optional(),
  notes: z.array(PurchaseOrderNoteSchema).optional(),
  audit: AuditResponseSchema.optional(),
  dateDeleted: z.coerce.date<Date>().optional(),
  deletedBy: z.string().optional(),
  deletedById: z.uuid().optional(),
  type: PurchaseOrderTypeEnum.optional(),
  orderId: z.uuid().optional(),
  purchaseOrderNumberFormat: PurchaseOrderNumberFormatEnum,
});

const CallOffPurchaseOrderSchema = PurchaseOrderBaseSchema.extend({
  startDate: z.coerce.date<Date>(),
  endDate: z.coerce.date<Date>(),
  purchaseOrderNumberFormat: z.literal('CallOff'),
});

const NonCallOffPurchaseOrderSchema = PurchaseOrderBaseSchema.extend({
  deliveryDate: z.coerce.date<Date>(),
  purchaseOrderNumberFormat: NonCallOffPurchaseOrderNumberFormatEnum,
});

export const PurchaseOrderSchema = z.discriminatedUnion(
  'purchaseOrderNumberFormat',
  [CallOffPurchaseOrderSchema, NonCallOffPurchaseOrderSchema]
);

export const ReplenishmentOrderSchema = PurchaseOrderBaseSchema.omit({
  items: true,
  paymentTerms: true,
  deliveryDate: true,
  deliveryNote: true,
  notes: true,
  audit: true,
  supplier: true,
  deliveryAddress: true,
}).extend({
  generatedAt: z.coerce.date<Date>().optional(),
  numberOfItems: z.number(),
  output: z.string(),
  supplier: z.object({
    id: z.uuid(),
    name: z.string().nullish(),
  }),
});

export const ReplenishmentSearchResponseSchema = createPaginatedResponseSchema(
  ReplenishmentOrderSchema
);

export const DoesPurchaseOrderExistRequestSchema = z.object({
  purchaseOrderNumber: z.string().min(1),
  serviceLocationId: z.uuid(),
});

export const PurchaseOrdersSearchRequestSchema = PagedRequestSchema.extend({
  serviceLocationId: z.string().optional(),
  poNumber: z.any().optional(),
  orderNumber: z.string().optional(),
  supplierId: z.string().optional(),
  deliveryDateFrom: z.coerce.date<Date>().optional(),
  deliveryDateTo: z.coerce.date<Date>().optional(),
  searchText: z.string().optional(),
  statuses: PurchaseOrderStatusEnum.array().optional(),
});

export const ReplenishmentSearchRequestSchema =
  PurchaseOrdersSearchRequestSchema.omit({
    deliveryDateFrom: true,
    deliveryDateTo: true,
  }).extend({
    dateCreatedFrom: z.coerce.date<Date>().optional(),
    dateCreatedTo: z.coerce.date<Date>().optional(),
  });

export const PurchaseOrderSearchNumberEnum =
  PurchaseOrdersSearchRequestSchema.pick({
    poNumber: true,
    orderNumber: true,
  }).keyof();

export const PurchaseSearchResponseSchema =
  createPaginatedResponseSchema(PurchaseOrderSchema);

export const PurchaseOrderUpdateStatusSchema = z.object({
  purchaseOrderId: z.uuid(),
  status: PurchaseOrderStatusEnum,
});

export const ReplenishmentBulkOrderStatusUpdateSchema = z.object({
  purchaseOrderIds: z.array(z.uuid()),
  status: PurchaseOrderStatusEnum,
});

export const ReplenishmentBulkOrderStatusUpdateResponseSchema = z.object({
  updatedIds: z.array(z.uuid()),
  skippedIdsValidationFailed: z.array(z.uuid()),
  skippedIdsInternalError: z.array(z.uuid()),
});

export const UpdatePurchaseOrderItemsSchema = z.object({
  poStatus: z.string().nullish(),
  purchaseOrderId: z.uuid(),
  data: z.object({
    items: PurchaseOrderCommonItemSchema.omit({
      stockUnits: true,
      latestDateDelivered: true,
    })
      .array()
      .nullish(),
  }),
});

export const UpdatePurchaseOrderItemsRequestSchema = z.object({
  purchaseOrderId: z.uuid(),
  items: PurchaseOrderCommonItemSchema.omit({
    stockUnits: true,
    latestDateDelivered: true,
  })
    .extend({ itemId: z.uuid() })
    .array()
    .nullish(),
});

export const PurchaseOrderLinkToOrderSchema = z.object({
  purchaseOrderId: z.uuid(),
  purchaseOrderItemId: z.uuid(),
  orderId: z.uuid(),
  jobId: z.uuid(),
  itemId: z.uuid(),
  partId: z.uuid(),
  syncQuantity: z.boolean(),
});

export const PurchaseOrderLinkToOrderFormPartsSchema = z.object({
  jobId: z.uuid(),
  itemId: z.uuid(),
  partId: z.uuid(),
  purchaseOrderItemId: z.uuid(),
});

export const PurchaseOrderLinkToOrderPartsErrorEnum = z.enum([
  'alreadyAssigned',
  'skuDoesNotExist',
  'quantityDoesNotMatch',
  'jobNotAuthorised',
  'serviceLocationDoesNotMatch',
]);

export const BulkActionEnum = z.enum(['confirmApproval', 'confirmCancel']);

export const PurchaseOrderShortShippedTooltipKeyEnum = z.enum([
  'shortShippedNotAvailableForCallOff',
  'shortShippedRequiresReceivedItem',
]);

export type PurchaseOrderShortShippedTooltipKey = z.infer<
  typeof PurchaseOrderShortShippedTooltipKeyEnum
>;

export const PurchaseOrderLinkToOrderFormSchema = z.object({
  purchaseOrderId: z.uuid(),
  orderId: z.uuid(),
  orderStatus: z.string(),
  parts: PurchaseOrderLinkToOrderFormPartsSchema.array(),
});

export const PurchaseOrderLinkToOrderRequestSchema = z.object({
  links: PurchaseOrderLinkToOrderSchema.array(),
});

export const AssignPurchaseOrderToOrderSchema = z.object({
  jobId: z.uuid(),
  links: PurchaseOrderLinkToOrderSchema.array(),
  purchaseOrderId: z.uuid(),
});

export type PurchaseOrderIdModel = z.infer<typeof PurchaseOrderIdSchema>;
export type PurchaseOrderDeliveryDetailsUpdateModel = z.infer<
  typeof PurchaseOrderDeliveryDetailsUpdateSchema
>;
export type UpdatePurchaseOrderItemModel = z.infer<
  typeof UpdatePurchaseOrderItemSchema
>;
export type UpdatePurchaseOrderModel = z.infer<
  typeof UpdatePurchaseOrderSchema
>;
export type PurchaseOrderItemModel = z.infer<typeof PurchaseOrderItemSchema>;

export type PurchaseOrderFormItemModel = z.infer<
  typeof PurchaseOrderFormItemSchema
>;
export type PurchaseOrderFormModel = z.infer<typeof PurchaseOrderFormSchema>;

export type CreatePurchaseOrderItemModel = z.infer<
  typeof CreatePurchaseOrderItemSchema
>;
export type CreatePurchaseOrderModel = z.infer<
  typeof CreatePurchaseOrderSchema
>;
export type CreatePurchaseOrderFromJobRequestModel = z.infer<
  typeof CreatePurchaseOrderFromJobRequestSchema
>;

export type PurchaseStockOrderNoteRequestModel = z.infer<
  typeof PurchaseOrderNoteRequestSchema
>;

export type PurchaseOrderSearchRequestModel = z.infer<
  typeof PurchaseOrdersSearchRequestSchema
>;

export type ReplenishmentSearchRequestModel = z.infer<
  typeof ReplenishmentSearchRequestSchema
>;

export type PurchaseOrderSearchNumberType = z.infer<
  typeof PurchaseOrderSearchNumberEnum
>;

export type DoesPurchaseOrderExistRequestModel = z.infer<
  typeof DoesPurchaseOrderExistRequestSchema
>;

export type PurchaseOrderModel = z.infer<typeof PurchaseOrderSchema>;

export type PurchaseOrderSearchResponseModel = z.infer<
  typeof PurchaseSearchResponseSchema
>;

export type PurchaseOrderUpdateStatusModel = z.infer<
  typeof PurchaseOrderUpdateStatusSchema
>;
export type ReplenishmentBulkOrderStatusUpdateModel = z.infer<
  typeof ReplenishmentBulkOrderStatusUpdateSchema
>;
export type ReplenishmentBulkOrderStatusUpdateResponseModel = z.infer<
  typeof ReplenishmentBulkOrderStatusUpdateResponseSchema
>;
export type GetPurchaseOrderModel = z.infer<typeof GetPurchaseOrderSchema>;

export type UpdatePurchaseOrderItemsModel = z.infer<
  typeof UpdatePurchaseOrderItemsSchema
>;

export type PurchaseOrderLinkToOrderModel = z.infer<
  typeof PurchaseOrderLinkToOrderSchema
>;
export type PurchaseOrderLinkToOrderFormPartsModel = z.infer<
  typeof PurchaseOrderLinkToOrderFormPartsSchema
>;
export type PurchaseOrderLinkToOrderFormModel = z.infer<
  typeof PurchaseOrderLinkToOrderFormSchema
>;
export type PurchaseOrderLinkToOrderPartsErrorType = z.infer<
  typeof PurchaseOrderLinkToOrderPartsErrorEnum
>;
export type PurchaseOrderLinkToOrderRequestModel = z.infer<
  typeof PurchaseOrderLinkToOrderRequestSchema
>;

export type ReplenishmentOrderModel = z.infer<typeof ReplenishmentOrderSchema>;

export type ReplenishmentSearchResponseModel = z.infer<
  typeof ReplenishmentSearchResponseSchema
>;
export type AssignPurchaseOrderToOrderModel = z.infer<
  typeof AssignPurchaseOrderToOrderSchema
>;
export type UpdatePurchaseOrderItemsRequestModel = z.infer<
  typeof UpdatePurchaseOrderItemsRequestSchema
>;
export type PurchaseOrderStatus = z.infer<typeof PurchaseOrderStatusEnum>;

export const GetPurchaseOrdersByJobRequestSchema = z.object({
  jobIds: z.array(z.uuid()),
  statuses: z.array(PurchaseOrderStatusEnum),
});

export const GetPurchaseOrdersByJobResponseItemSchema = z.object({
  id: z.uuid(),
  orderNumber: z.string().nullish(),
  number: z.string().nullish(),
  jobIds: z.array(z.uuid()),
  partIds: z.array(z.uuid()),
});

export type GetPurchaseOrdersByJobRequestModel = z.infer<
  typeof GetPurchaseOrdersByJobRequestSchema
>;

export type GetPurchaseOrdersByJobResponseItemModel = z.infer<
  typeof GetPurchaseOrdersByJobResponseItemSchema
>;
